import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import {
  buttonStyles,
  type ButtonSize,
  type ButtonVariant,
} from './buttonStyles';
import { Spinner } from './Spinner';
import { Tooltip } from './Tooltip';
import { useButtonState } from './useButtonState';

export interface IconButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'aria-label' | 'children'
> {
  /** Required accessible name; also the default tooltip. */
  'aria-label': string;
  /** A single icon, e.g. `<Settings />`. Sized by the button. */
  icon: ReactNode;
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Tooltip content. Defaults to `aria-label`; pass `false` to hide it. */
  tooltip?: ReactNode | false;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  loading?: boolean;
  /** Why the button is disabled; added to the tooltip and description. */
  disabledReason?: ReactNode;
}

/** Square, icon-only button. Defaults to the quiet `ghost` variant. */
export const IconButton = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  IconButtonProps
>(function IconButton(
  {
    icon,
    variant = 'ghost',
    size = 'md',
    tooltip,
    tooltipSide = 'top',
    loading = false,
    disabled,
    disabledReason,
    onClick,
    className,
    type = 'button',
    'aria-describedby': describedBy,
    ...props
  },
  ref
) {
  const { hasReason, reasonId, styleState, buttonProps } = useButtonState({
    disabled,
    disabledReason,
    loading,
    onClick,
    describedBy,
  });
  const label = props['aria-label'];
  let tooltipContent: ReactNode = null;
  if (tooltip !== false) {
    tooltipContent = hasReason ? (
      <>
        <span className="block">{tooltip ?? label}</span>
        <span className="block font-normal opacity-80">{disabledReason}</span>
      </>
    ) : (
      (tooltip ?? label)
    );
  }

  const button = (
    <button
      ref={ref}
      type={type}
      className={buttonStyles({
        variant,
        size,
        iconOnly: true,
        ...styleState,
        className,
      })}
      {...props}
      {...buttonProps}
    >
      {loading ? <Spinner size="sm" /> : icon}
    </button>
  );

  return (
    <>
      <Tooltip content={tooltipContent} side={tooltipSide}>
        {button}
      </Tooltip>
      {hasReason && (
        <span id={reasonId} className="sr-only">
          {disabledReason}
        </span>
      )}
    </>
  );
});
